import { ReactElement } from "react";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { KnowledgeItemPreviewItemModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { HubItem } from "./HubItem/HubItem";
import { ActiveFilter } from "client/Features/Hub/common";
import { MediaQueryKind, useWorkspaceMediaQuery } from "client/Hooks";
import { isNotNil } from "client/Misc/utils";
import { ListLayout, ListLayoutCollapseMode } from "client/Features/Hub/ListLayout/ListLayout";
import { TimeZone } from "client/DateAndTime/timezones";

export type ListProps = {
	items: readonly KnowledgeItemPreviewItemModel[];
	trackingId: string;
	onNavigateToItem(id: number): void;
	getCategory(categoryId: number | null): HubItemCategoryModel | undefined;
	onActiveFilterChange(filter: ActiveFilter): void;
	onSelectedCategoryIdChange(categoryId: number): void;
	timezone: TimeZone;
} & (
	{ hasMoreItems?: false; }
	|
	{
		hasMoreItems: boolean;
		isBusy: boolean;
		onLoadmore: () => void;
	}
) & (
	{
		collapseMode?: undefined;
	}
	|
	{
		collapseMode: ListLayoutCollapseMode;
		renderMoreButton: ReactElement;
		shouldAlwaysRenderMoreButton?: boolean;
	}
);

export const List = (props: ListProps): ReactElement => {
	const {
		items,
		trackingId,
		onNavigateToItem,
		getCategory,
		onActiveFilterChange,
		onSelectedCategoryIdChange,
		timezone
	} = props;

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	// Using server side pagination it is possible that the data of item(s) changes resulting
	// that the order of the items in the set changes, which makes it that client side you could
	// have multiple items with the same id. To counter this, we suffix the number of the id duplicate.
	const idCounters = new Map<number, number>();
	const getKey = (id: number): string | number => {
		const occurance: number = (idCounters.get(id) ?? 0) + 1;

		idCounters.set(
			id,
			occurance
		);

		if (occurance === 1) {
			return id;
		}

		return `${id}-${occurance}`;
	};

	return (
		<ListLayout
			trackingId={trackingId}
			{...(
				props.collapseMode !== undefined
					? {
						collapseMode: props.collapseMode,
						renderMoreButton: props.renderMoreButton,
						shouldAlwaysRenderMoreButton: props.shouldAlwaysRenderMoreButton
					}
					: {
						collapseMode: undefined
					}
			)}
			hasBorderTop={isMobile}
			renderAfterItems={props.hasMoreItems
				? (
					<ListLayout.LoadMoreIndicator
						onRequestMoreItems={props.onLoadmore}
						hasMoreItems={props.hasMoreItems}
						isLoadingData={props.isBusy}
					/>
				)
				: undefined
			}
		>
			{items.map(item => (
				<ListLayout.Item key={getKey(item.id)}>
					<HubItem
						title={item.title}
						descriptionPreview={item.descriptionPreview}
						media={item.media}
						lastModifiedDate={item.lastModifiedDate}
						category={getCategory(item.category?.id ?? null)}
						author={item.author}
						numberOfViews={item.viewsCount}
						tags={item.tags}
						onNavigate={() => onNavigateToItem(item.id)}
						onActiveFilterChange={onActiveFilterChange}
						onSelectedCategoryIdChange={onSelectedCategoryIdChange}
						trackingId={`knowledge-item-card|${item.id}`}
						isPinned={isNotNil(item.pinDate)}
						isMobile={isMobile}
						timezone={timezone}
						isNew={item.isNew}
					/>
				</ListLayout.Item>
			))}
		</ListLayout>
	);
};

List.displayName = "List";
